import LegalLayout from "../components/LegalLayout";

export default function TermsOfService() {
  return (
    <LegalLayout title="Terms of Service" updated="8 October 2026">
      <p>
        These Terms of Service (&ldquo;Terms&rdquo;) govern your access to and use of Groundwork TierUp (the &ldquo;Service&rdquo;),
        operated by Groundwork TierUp (ABN 16 442 280 390) (&ldquo;we&rdquo;, &ldquo;us&rdquo;, &ldquo;our&rdquo;). By creating an account
        or using the Service, you agree to these Terms. If you&rsquo;re agreeing on behalf of a company, you confirm you have authority
        to bind that company.
      </p>

      <h2>1. The Service</h2>
      <p>
        Groundwork TierUp is a software-as-a-service platform that helps delivery leaders connect project management, change management,
        and business improvement processes in one place, including tools such as the Concept to Charter workflow.
      </p>

      <h2>2. Accounts</h2>
      <p>
        You must provide accurate information when creating an account and keep your login credentials confidential. You&rsquo;re
        responsible for all activity under your account. Tell us immediately at{" "}
        <a href="mailto:hello@groundworktierup.com.au">hello@groundworktierup.com.au</a> if you suspect unauthorised access.
      </p>

      <h2>3. Subscriptions, fees, and billing</h2>
      <ul>
        <li>
          Access to the Service requires a paid subscription. The current plan is Groundwork TierUp Practitioner, billed monthly or
          annually. Groundwork TierUp Professional is coming soon and is not yet available to purchase. The current plan and pricing are
          listed at{" "}
          <a href="/#pricing">groundworktierup.com.au/#pricing</a>.
        </li>
        <li>Subscriptions <strong>automatically renew</strong> at the end of each billing period unless cancelled beforehand, in accordance with our <a href="/cancellation">Cancellation Policy</a>.</li>
        <li>Fees are charged in advance via our payment processor, Stripe. By subscribing, you authorise us to charge your nominated payment method each billing period.</li>
        <li>If you switch plans or billing intervals, charges and credits are prorated for the remainder of the current billing period and applied immediately.</li>
        <li>We may change our pricing with reasonable advance notice (e.g. 30 days); changes take effect from your next renewal.</li>
        <li>Prices shown do not currently include GST. Groundwork TierUp is not currently registered for GST. If that changes, we&rsquo;ll update this clause and our pricing before GST is added to any charge.</li>
      </ul>

      <h2>4. Cancellation and refunds</h2>
      <p>Cancellations and refunds are governed by our <a href="/cancellation">Cancellation Policy</a>, which forms part of these Terms.</p>

      <h2>5. Acceptable use</h2>
      <p>You agree not to:</p>
      <ul>
        <li>use the Service for any unlawful purpose or in breach of any law;</li>
        <li>attempt to gain unauthorised access to the Service, other accounts, or our systems;</li>
        <li>reverse-engineer, resell, or white-label the Service without our written permission;</li>
        <li>upload content that infringes another party&rsquo;s intellectual property or is defamatory, harmful, or unlawful;</li>
        <li>interfere with the Service&rsquo;s operation or security (e.g. through malware, scraping, or excessive automated requests).</li>
      </ul>
      <p>We may suspend or terminate accounts that breach this section.</p>

      <h2>6. Your data and content</h2>
      <p>
        You retain ownership of the project, charter, and other content you input into the Service (&ldquo;Your Data&rdquo;). You grant
        us a limited licence to host, store, process, and display Your Data solely to provide and improve the Service. We won&rsquo;t use
        Your Data for any other purpose, and won&rsquo;t sell it. See our <a href="/privacy">Privacy Policy</a> for how personal
        information within Your Data is handled.
      </p>
      <p>
        You&rsquo;re responsible for the content you and anyone you invite put into Groundwork TierUp, including having the right to
        upload it and to record information about other people. Don&rsquo;t record health, criminal or other sensitive personal
        information about individuals unless you&rsquo;re permitted to. The Service and the data you enter into it are hosted on
        servers located in Australia.
      </p>

      <h2>7. Our intellectual property</h2>
      <p>
        The Service, including its software, design, branding, and underlying technology, is owned by us or our licensors and protected
        by intellectual property law. These Terms don&rsquo;t grant you any rights to our IP beyond the limited right to use the Service
        as intended.
      </p>

      <h2>8. Service availability</h2>
      <p>
        We aim to keep Groundwork TierUp available and reliable, but we don&rsquo;t guarantee uninterrupted or error-free operation. We
        may need to suspend access for maintenance, updates, or circumstances outside our control, and we&rsquo;ll aim to give
        reasonable notice where practical.
      </p>

      <h2>9. Warranties and liability</h2>
      <p>
        To the maximum extent permitted by the Australian Consumer Law, the Service is provided &ldquo;as is.&rdquo; Nothing in these
        Terms excludes, restricts, or modifies any consumer guarantee, right, or remedy you have under the Australian Consumer Law that
        cannot lawfully be excluded. Subject to that, to the extent permitted by law, we exclude all other warranties, and our total
        liability arising from your use of the Service is limited to the amount you paid us in the 12 months before the claim arose.
      </p>
      <p>
        We&rsquo;re not liable for indirect, consequential, or special damages, including loss of profits or data, to the extent
        permitted by law.
      </p>

      <h2>10. Termination</h2>
      <p>
        We may suspend or terminate your access if you materially breach these Terms and don&rsquo;t fix the breach within a reasonable
        period after we notify you, or immediately for serious breaches (e.g. unlawful use, non-payment). You may cancel at any time as
        described in our <a href="/cancellation">Cancellation Policy</a>.
      </p>

      <h2>11. Changes to these Terms</h2>
      <p>
        We may update these Terms from time to time. We&rsquo;ll post the updated version here with a new &ldquo;last updated&rdquo;
        date, and notify active subscribers of material changes by email. Continued use of the Service after changes take effect means
        you accept the updated Terms.
      </p>

      <h2>12. Governing law</h2>
      <p>These Terms are governed by the laws of South Australia, Australia, and both parties submit to the non-exclusive jurisdiction of its courts.</p>

      <h2>13. Contact</h2>
      <p>
        Questions about these Terms? Email <a href="mailto:hello@groundworktierup.com.au">hello@groundworktierup.com.au</a>.
      </p>
    </LegalLayout>
  );
}
